"use strict";

const InputBaseControlComponent = require("../../InputBaseControlComponent");
const _ = require("lodash");

class InputBaseSingleOptionSelectionControlComponent extends InputBaseControlComponent {
    /**
     * @schema.name Input Base Single Option Selection Control
     *
     * @schema.configuration.options.name options
     * @schema.configuration.options.type array
     * @schema.configuration.options.default []
     * @schema.configuration.options.required false
     *
     * @schema.configuration.value.name value
     * @schema.configuration.value.type string|object|boolean|number
     * @schema.configuration.value.default undefined
     * @schema.configuration.value.required false
     *
     * @schema.configuration.userSelected.name value
     * @schema.configuration.userSelected.type string|object|boolean|number
     * @schema.configuration.userSelected.default undefined
     * @schema.configuration.userSelected.required false
     */

    constructor(configOptions) {
        super(configOptions);
        this._userSelected = _.get(configOptions, "userSelected", undefined);
        this.setOptions(_.get(configOptions, "options", []));
    }
    /*******************************************/
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * @schema.methods.setOptions.name setOptions
     * @schema.methods.setOptions.description Sets the options used within the control and returns an instance of the control for chainability
     * @schema.methods.setOptions.returns Instance of the control
     * @schema.methods.setOptions.parameters.newVal.name newVal
     * @schema.methods.setOptions.parameters.newVal.description An array of option configuration objects containing 'label' and 'value' properties
     * @schema.methods.setOptions.parameters.newVal.type array
     * @schema.methods.setOptions.parameters.newVal.arrayObjectValue.label.name label
     * @schema.methods.setOptions.parameters.newVal.arrayObjectValue.label.description A string serving as the display text for each option
     * @schema.methods.setOptions.parameters.newVal.arrayObjectValue.label.type string
     * @schema.methods.setOptions.parameters.newVal.arrayObjectValue.value.name value
     * @schema.methods.setOptions.parameters.newVal.arrayObjectValue.value.description The value for each option that can be string|object|boolean|number
     * @schema.methods.setOptions.parameters.newVal.arrayObjectValue.value.type string|object|boolean|number
     * @schema.methods.setOptions.parameters.newVal.arrayObjectValue.disabled.name disabled
     * @schema.methods.setOptions.parameters.newVal.arrayObjectValue.disabled.description A boolean representing the disabled state of the option (optional)
     * @schema.methods.setOptions.parameters.newVal.arrayObjectValue.disabled.type boolean
     * @schema.methods.setOptions.parameters.newVal.arrayObjectValue.disabled.required false
     */
    setOptions(newVal) {
        if (newVal instanceof Array) {
            let optionFormatBroken = false;
            newVal.forEach((configObject) => {
                if (_.isUndefined(configObject.value) || _.isUndefined(configObject.label)) {
                    if (_.isUndefined(configObject.name)) {
                        console.error(
                            `At a minimum, each object for the "options" property of this control must contain a "value" and "label" property.`
                        );
                        optionFormatBroken = true;
                    } else {
                        if (_.isUndefined(configObject.value)) {
                            configObject.value = configObject.name;
                        }
                        if (_.isUndefined(configObject.label)) {
                            configObject.label = configObject.name;
                        }
                    }
                }
            });
            if (!optionFormatBroken) {
                this._options = newVal;
                this.emit("Options", this._options);
            }
        } else {
            console.error("Options for this control are not in an array.");
        }
        return this;
    }
    /**
     * @schema.methods.getOptions.name getOptions
     * @schema.methods.getOptions.description Returns the array of options used for the control
     * @schema.methods.getOptions.returns array
     */
    getOptions() {
        return this._options;
    }
    get options() {
        return this.getOptions();
    }
    set options(newArray) {
        return this.setOptions(newArray);
    }

    /**
     * @schema.methods.setSelectedOption.name setSelectedOption
     * @schema.methods.setSelectedOption.description Sets the passed in value selected within the control and returns an instance of the control for chainability
     * @schema.methods.setSelectedOption.returns Instance of the control
     * @schema.methods.setSelectedOption.parameters.value.name newVal
     * @schema.methods.setSelectedOption.parameters.value.description The value for the new selection that can be string|object|boolean|number matching the 'value' property of an option
     * @schema.methods.setSelectedOption.parameters.value.type string|object|boolean|number
     */
    setSelectedOption(value) {
        this.setValue(value);
    }

    /**
     * @schema.methods.setValue.name setValue
     * @schema.methods.setValue.description Sets the value and returns an instance of the control for chainability
     * @schema.methods.setValue.returns Instance of the control
     * @schema.methods.setValue.parameters.newVal.name newVal
     * @schema.methods.setValue.parameters.newVal.description The new value
     * @schema.methods.setValue.parameters.newVal.type string|object|boolean|number
     * @schema.methods.setValue.parameters.wasTriggeredByUser.name wasTriggeredByUser
     * @schema.methods.setValue.parameters.wasTriggeredByUser.description Flag alerting that this was a user-triggered change (optional)
     * @schema.methods.setValue.parameters.wasTriggeredByUser.required false
     * @schema.methods.setValue.parameters.wasTriggeredByUser.type boolean
     */
    setValue(newVal, wasTriggeredByUser) {
        let oldVal = _.cloneDeep(this._value);
        this._value = newVal;
        this.emit("Value", this._value, oldVal, wasTriggeredByUser);
        return this;
    }
    /**
     * @schema.methods.getValue.name getValue
     * @schema.methods.getValue.description Returns the value of the control
     * @schema.methods.getValue.returns string|object|boolean|number
     */
    getValue() {
        return this._value;
    }
    get value() {
        return this.getValue();
    }
    set value(newVal) {
        return this.setValue(newVal);
    }

    /**
     * @schema.methods.setUserSelected.name setUserSelected
     * @schema.methods.setUserSelected.description Sets the passed in value into a 'user selected' (highlighted) state
     * @schema.methods.setUserSelected.returns Instance of the control
     * @schema.methods.setUserSelected.parameters.value.name newVal
     * @schema.methods.setUserSelected.parameters.value.description The new value
     * @schema.methods.setUserSelected.parameters.value.type string|object|boolean|number
     */
    setUserSelected(value) {
        this._userSelected = value;
        this.emit("UserSelected", this._userSelected);
        return this;
    }
    /**
     * @schema.methods.getUserSelected.name getUserSelected
     * @schema.methods.getUserSelected.description Returns the userSelected value of the control
     * @schema.methods.getUserSelected.returns string|object|boolean|number
     */
    getUserSelected() {
        return this._userSelected;
    }
    get userSelected() {
        return this.getUserSelected();
    }
    set userSelected(val) {
        return this.setUserSelected(val);
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = InputBaseSingleOptionSelectionControlComponent;
